import {
    Avatar,
    Colors,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { memo, type ReactNode } from 'react';

import type { TextAnswer } from '@/components/QuestionResultContainer/types';

import { useAuthorSignature } from '@/hooks/useAuthorSignature';
import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import { getFullName } from '@/utils/user';

export interface CommentAnswerCardProps {
    answer: TextAnswer;
    userAnswer?: string;
    isReply?: boolean;
    /** Drawn inside the bubble, under the text: the way to reply to it. */
    footer?: ReactNode;
    className?: string;
    dataTestId?: string;
}

export const CommentAnswerCard = memo(
    ({
        answer,
        userAnswer,
        isReply = false,
        footer,
        className = '',
        dataTestId = 'comment-answer-card',
    }: CommentAnswerCardProps) => {
        const { t } = useAmelioTranslations();
        const signature = useAuthorSignature({
            authorName: answer.user && getFullName(answer.user),
            authorPictureUrl: answer.user?.profilePictureUrl,
            isAnonymousRespondent: answer.isAnonymousRespondent,
            writtenAt: answer.submittedAt,
        });

        return (
            <div className={`flex items-start gap-3 ${className}`}>
                <TooltipProvider>
                    <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                            <div
                                className={'shrink-0 cursor-default'}
                                data-testid={`${dataTestId}-avatar-trigger`}
                            >
                                <Avatar
                                    alt={signature.name}
                                    initials={signature.initials}
                                    src={signature.pictureUrl}
                                    size={'xs'}
                                    dataTestId={`${dataTestId}-avatar`}
                                />
                            </div>
                        </TooltipTrigger>

                        <TooltipContent side={'top'}>
                            <p>{signature.name}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                <div
                    className={`border-base-100 flex min-w-0 flex-1 flex-col rounded-lg border bg-white p-2`}
                    data-testid={dataTestId}
                >
                    <div className={'items-start justify-between sm:flex'}>
                        <span
                            className={'min-w-0 flex-1'}
                            data-testid={`${dataTestId}-reviewer-name`}
                        >
                            <Text
                                color={Colors.BASE}
                                weight={'semibold'}
                                className={'leading-tight wrap-break-word'}
                            >
                                {signature.name}
                            </Text>
                        </span>

                        {signature.writtenAgo && (
                            <span
                                className={'shrink-0'}
                                data-testid={`${dataTestId}-date`}
                            >
                                <Text
                                    color={Colors.BASE_400}
                                    size={'small'}
                                >
                                    {signature.writtenAgo}
                                </Text>
                            </span>
                        )}
                    </div>

                    {userAnswer && !isReply && (
                        <Text
                            color={Colors.BASE_600}
                            size={'small'}
                            className={'mt-1 wrap-break-word'}
                        >
                            {`${t('questionResult.userAnswer')} ${userAnswer}`}
                        </Text>
                    )}

                    <span
                        data-testid={`${dataTestId}-text`}
                        className={'block'}
                    >
                        <Text
                            color={Colors.BASE}
                            className={'mt-2 leading-relaxed break-words whitespace-pre-wrap'}
                        >
                            {answer.text}
                        </Text>
                    </span>

                    {footer && <div className={'mt-2'}>{footer}</div>}
                </div>
            </div>
        );
    },
);

CommentAnswerCard.displayName = 'CommentAnswerCard';
